import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { logOutUser, extendSession as extendSessionAction } from "src/redux/actions/user";

interface UseSessionTimeoutProps {
  loginDate: string | null;
  isLoggedIn: boolean;
  sessionTimeoutDays?: number;
  warningMinutes?: number; // Show warning X minutes before timeout
}

interface UseSessionTimeoutReturn {
  showWarning: boolean;
  remainingTime: number; // in seconds
  extendSession: () => void;
  closeWarning: () => void;
}

export const useSessionTimeout = ({
  loginDate,
  isLoggedIn,
  sessionTimeoutDays = 7,
  warningMinutes = 5,
}: UseSessionTimeoutProps): UseSessionTimeoutReturn => {
  const dispatch = useDispatch();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());

  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const logoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Convert session timeout to milliseconds
  const sessionTimeoutMs = sessionTimeoutDays * 24 * 60 * 60 * 1000;
  const warningTimeMs = warningMinutes * 60 * 1000;

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (activityTimeoutRef.current) clearTimeout(activityTimeoutRef.current);
  }, []);

  const handleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    dispatch(logOutUser());
  }, [dispatch, clearAllTimers]);

  const extendSession = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    setLastActivity(Date.now());
    // Update Redux state to extend the session
    // This will trigger the useEffect that monitors loginDate and restart timers
    dispatch(extendSessionAction());
  }, [clearAllTimers, dispatch]);

  const closeWarning = useCallback(() => {
    setShowWarning(false);
  }, []);

  const startCountdown = useCallback((timeUntilLogout: number) => {
    setRemainingTime(Math.floor(timeUntilLogout / 1000));

    countdownTimerRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const setupTimers = useCallback(() => {
    if (!loginDate || !isLoggedIn) return;

    clearAllTimers();

    const loginTime = new Date(loginDate).getTime();
    const currentTime = Date.now();
    const elapsedTime = currentTime - loginTime;

    // Check if session has already expired
    if (elapsedTime >= sessionTimeoutMs) {
      handleLogout();
      return;
    }

    const timeUntilWarning = sessionTimeoutMs - warningTimeMs - elapsedTime;
    const timeUntilLogout = sessionTimeoutMs - elapsedTime;

    // Setup warning timer
    if (timeUntilWarning > 0) {
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
        startCountdown(warningTimeMs);
      }, timeUntilWarning);
    } else {
      // We're already in the warning period
      const timeLeft = timeUntilLogout;
      if (timeLeft > 0) {
        setShowWarning(true);
        startCountdown(timeLeft);
      } else {
        handleLogout();
        return;
      }
    }

    // Setup logout timer
    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, timeUntilLogout);
  }, [loginDate, isLoggedIn, sessionTimeoutMs, warningTimeMs, handleLogout, startCountdown, clearAllTimers]);

  // Track user activity
  useEffect(() => {
    if (!isLoggedIn) return;

    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    const updateActivity = () => {
      setLastActivity(Date.now());
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [isLoggedIn]);

  // Reset timers when user is active
  useEffect(() => {
    if (!isLoggedIn || showWarning) return;

    // Only reset if user was recently active (within last 30 seconds)
    // This prevents constant timer resets
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }

    activityTimeoutRef.current = setTimeout(() => {
      setupTimers();
    }, 100);
  }, [lastActivity, isLoggedIn, showWarning, setupTimers]);

  // Initial setup
  useEffect(() => {
    setupTimers();
    return () => {
      clearAllTimers();
    };
  }, [loginDate, isLoggedIn, setupTimers, clearAllTimers]);

  return {
    showWarning,
    remainingTime,
    extendSession,
    closeWarning,
  };
};
